/******************************************************************************************
 * * INFOCARD COMPONENT
 * ---------------------------------------------------------------------------------------
 * The InfoCard component is used to display a small amount of concise information on
 * a card. This is a hightlight card that is used to draw attention to a specific piece
 * of information. This component is used in the portable text serializer to render
 * the infoCard object.
 * ****************************************************************************************/
import React from 'react';
import { InfoCardProps } from './InfoCard.types';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import { useDevMode } from 'src/hooks';

const InfoCard = ({ value }: InfoCardProps) => {
  // Determine if you are in dev mode
  const isDev = useDevMode();

  // Get the theme
  const theme = useTheme();
  const primary = theme.palette.primary.light;
  const secondary = theme.palette.secondary.light;
  const contrastText = theme.palette.text.primary;

  const checkmark = (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="12" viewBox="0 0 14 12" fill="none">
      <path d="M1.5 5.27778L5.5222 9.25L12.75 1" stroke={contrastText} stroke-width="2.5" />
    </svg>
  );

  return (
    <Box
      border={isDev ? '1px solid tomato' : undefined}
      display="flex"
      gap={value?.gap ?? '20px'}
      alignItems="flex-start" // Ensures vertical centering of children
    >
      <Box
        border={isDev ? '1px solid cyan' : undefined}
        width={value?.imageWidth ?? '100px'}
        height="auto"
        display="flex" // Adding flex to center the IMG vertically and horizontally
        alignItems="center"
        justifyContent="center"
      >
        <Box
          className="info-box-icon-background"          
          sx={{
            mt: value?.image?.asset ? 'unset' : '2px',
            background: value.image?.asset?.url ? secondary : primary,
            width: value?.imageWidth ?? '100px',
            height: value?.imageWidth ?? '100px',
            borderRadius: '50%',
            display: 'flex', // Centering IMG
            alignItems: 'center',
            justifyContent: 'center',
            content: '""',
          }}
        >          
          {value.image?.asset?.url ? <img src={value.image.asset.url} alt={value.image.alt} /> : checkmark}
        </Box>
      </Box>
      <Stack border={isDev ? '1px solid cyan' : undefined} spacing={1}>
        <Typography variant={value?.titleVariant ?? 'body1'}>{value.title}</Typography>
        <Typography variant={value?.descriptionVariant ?? 'body2'}>{value.description}</Typography>
      </Stack>
    </Box>
  );
};

export default InfoCard;
